import dayjs from "dayjs";
import commonservice from "@apis/commonservice";
import { DEFAULT_CODE, DEFAULT_CURRENCY, DEFAULT_LANGUAGE, LOCALSTORAGE_KEY } from "@config";
import { useAppSelector } from "@hooks/redux";
import useUserInfo from "@hooks/userInfo";
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import defaultIpInfo from "@src/config/defaultIpInfo";
import TestModal from "@src/test";
import { getRegionIcon } from "@utils/format";
import { initPageLanguage, initTrackCode, toFirstUpCase } from "@utils/util";
import { CreditInfo, GlobalState, IUserinfo } from "./index";
import { MemberShip, PRICING_LEVEL } from "./memberCenterSlice";
import { RootState } from "./store";
import aiStudioService from "@apis/aiStudioService";
import { syncSentryUser, clearSentryUser } from "@kalodata/sentry";

let initloginMethod = localStorage.getItem(LOCALSTORAGE_KEY.LOGIN_METHOD) as LOGIN_METHODS;

export const initLoginMethod = (): LOGIN_METHODS => {
  // const language = localStorage.getItem(LOCALSTORAGE_KEY.LANGUAGE) || DEFAULT_LANGUAGE;
  // if (useWxScodeLogin({ language })) {
  //   return "wxcode";
  // }
  // 美国ip或者其他国家ip 并且没有记录过登录方式 默认用email
  if (defaultIpInfo.signupType === "email" && (!initloginMethod || initloginMethod === "wxcode"))
    return "email_password";

  // 其他情况用 sms
  return initloginMethod || "password";
};

export const initSignupMethod = (): "sms" | "email" | "wxcode" => {
  // const language = localStorage.getItem(LOCALSTORAGE_KEY.LANGUAGE) || DEFAULT_LANGUAGE;
  // if (useWxScodeLogin({ language })) return "wxcode";
  // return defaultIpInfo.signupType;
  return "sms";
};

const initialState: GlobalState = {
  deviceId: undefined,
  username: localStorage.getItem("userName") || "visitor",
  isLogin: undefined,
  i18Ready: true,
  language: initPageLanguage(),
  trackCode: initTrackCode(),
  pageLoading: true,
  userInfo: {
    username: localStorage.getItem("userName") || "visitor",
    email: "",
    emailVerified: false,
    companySize: "",
    identity: "",
    currency: DEFAULT_CURRENCY,
    language: DEFAULT_LANGUAGE,
    industry: [""],
    phoneNumber: "",
    phoneCountryCode: DEFAULT_CODE() || "",
    completed: false,
    uid: "",
    innerUser: -1,
    requirement: "[]",
    requirementIds: "",
    agencyWhite: 0,
    closeNoticeStatus: 1,
    subAccount: false,
    havePremium: false,
    phoneVerified: false,
    tiktokVerified: false,
    googleVerified: false,
    googleId: "",
    googleName: "",
    googleEmail: "",
    passwordExist: false,
    firstName: "",
    lastName: "",
    mainUsername: "",
    tiktokUserHandle: "",
    tiktokUserId: "",
    tags: [],
    bizCountry: "",
    isCharmCountry: false,
    avatarUrl: "",
    haveStandard: false,
    wxVerified: 0,
  },
  mediaUploadAgreement: {
    agreed: false,
    currentVersion: "",
  },
  creditInfo: {
    monthlyRemain: 0,
    permanentRemain: 0,
    totalRemain: 0,
    subAccount: false,
    mainTotalRemain: 0,
  },
  creditLoading: false,
  imageHover: {
    type: "image",
    isShow: false,
  },
  showCode: false,
  membership: null,
  redPointVisible: false,
  completeInfoVisible: false,
  completeDone: false,
  isTimesLimitVis: false,
  isScreenlt720: false,
  loginMethod: initLoginMethod(),
  signupMethod: initSignupMethod(),
  submitLoading: false,
  bindContactInformation: false,
  region: localStorage.getItem(LOCALSTORAGE_KEY.REGION) || "",
  currency: DEFAULT_CURRENCY,
  lastDay: "",
  firstDay: "",
};

export const queryMembership = createAsyncThunk("global/queryMembership", async (reqData: unknown) => {
  return await commonservice.queryMembership<MemberShip>(reqData);
});

export const fetchAiCredits = createAsyncThunk("global/fetchAiCredits", async () => {
  return await aiStudioService.fetchAiCredits<CreditInfo>();
});

export const globalSlice = createSlice({
  name: "global",
  initialState: initialState,
  reducers: {
    updateCreditInfo: (state, action: PayloadAction<CreditInfo>) => {
      state.creditInfo = action.payload;
    },
    updateSubmitLoading: (state, action: PayloadAction<boolean>) => {
      state.submitLoading = action.payload;
    },
    updateBindContactInformation: (state, action: PayloadAction<boolean>) => {
      state.bindContactInformation = action.payload;
    },
    updateLoginMethod: (state, action: PayloadAction<"sms" | "email" | "password" | "email_password" | "wxcode">) => {
      state.loginMethod = action.payload;
    },
    updateSignupMethod: (state, action: PayloadAction<"sms" | "email" | "wxcode">) => {
      state.signupMethod = action.payload;
    },
    updateDeviceId: (state, action: PayloadAction<string>) => {
      state.deviceId = action.payload;
    },
    updatePageLoading: (state, action: PayloadAction<boolean>) => {
      state.pageLoading = action.payload;
    },
    updateCompleteInfoVisible: (state, action: PayloadAction<boolean>) => {
      state.completeInfoVisible = action.payload;
    },
    updateCompleteDone: (state, action: PayloadAction<boolean>) => {
      state.completeDone = action.payload;
    },
    updateLanguage: (state, action: PayloadAction<string>) => {
      localStorage.setItem(LOCALSTORAGE_KEY.LANGUAGE, action.payload);
      document.documentElement.lang = action.payload;
      state.language = action.payload;
    },
    updateIsScreenlt720: (state, action: PayloadAction<boolean>) => {
      state.isScreenlt720 = action.payload;
    },
    updateUserInfo: (state, action: PayloadAction<IUserinfo | null>) => {
      if (action.payload && action.payload.username) {
        state.userInfo = action.payload;

        state.isLogin = true;
        state.username = action.payload.username;
        const LANGUAGE = initPageLanguage();
        state.userInfo = action.payload;
        state.language = state.userInfo.language || LANGUAGE;
        localStorage.setItem(LOCALSTORAGE_KEY.LANGUAGE, state.language);
        localStorage.setItem("userName", state.userInfo.username);
        state.completeInfoVisible = !state.userInfo.completed;
        if (TestModal.completeInfoVisible) {
          state.completeInfoVisible = !!localStorage.getItem("completeInfoVisible");
        }
        try {
          if (import.meta.env.MODE === "production") {
            __bl.setConfig({
              setUsername: function () {
                return action.payload!.username;
              },
            });
          }
        } catch (err) {}
        const { username, uid, email, bizCountry, language } = action.payload;
        // Sync user to Sentry
        syncSentryUser(
          {
            id: uid,
            username,
            email,
            bizCountry,
            language,
            region: state.region,
          },
          "web",
        );
      } else {
        state.username = "visitor";
        state.isLogin = false;
        state.userInfo = initialState.userInfo;
        clearSentryUser();
      }
    },
    updateNames: (state, action) => {
      state.userInfo.firstName = action.payload[0] || "";
      state.userInfo.lastName = action.payload[1] || "";
    },
    updateCompleted: (state, action: PayloadAction<string>) => {
      state.userInfo.identity = action.payload;
    },
    updateRegion: (state, action: PayloadAction<REGION>) => {
      state.region = action.payload;
      localStorage.setItem(LOCALSTORAGE_KEY.REGION, action.payload);
    },
    clearGlobalStateSlice: (state: any) => {
      state.isLogin = false;
      state.userInfo = initialState.userInfo;
      clearSentryUser();
    },
    updateShowCode: (state, action: PayloadAction<boolean>) => {
      state.showCode = action.payload;
    },
    updateTimesLimit: (state, action: PayloadAction<boolean>) => {
      state.isTimesLimitVis = action.payload;
    },
    updateUserInfoAvatar: (state, action: PayloadAction<string>) => {
      state.userInfo.avatarUrl = action.payload;
    },
    updateMediaUploadAgreement: (state, action: PayloadAction<{ agreed: boolean; currentVersion: string }>) => {
      state.mediaUploadAgreement = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(queryMembership.pending, (state, _action) => {
        if (_action.payload) {
          state.membership = null;
        }
      })
      .addCase(queryMembership.fulfilled, (state, action) => {
        if (action.payload) {
          state.membership = { ...action.payload, level: toFirstUpCase(action.payload.level) as PRICING_LEVEL };
        }
      })
      .addCase(fetchAiCredits.pending, (state, _action) => {
        state.creditLoading = true;
      })
      .addCase(fetchAiCredits.fulfilled, (state, action: any) => {
        state.creditInfo = action.payload || {
          monthlyRemain: 0,
          permanentRemain: 0,
          totalRemain: 0,
        };
        state.creditLoading = false;
      })
      .addCase(fetchAiCredits.rejected, (state, _action) => {
        state.creditInfo = {
          monthlyRemain: 0,
          permanentRemain: 0,
          totalRemain: 0,
          subAccount: false,
          mainTotalRemain: 0,
        };
        state.creditLoading = false;
      });
  },
});

export const {
  updateCreditInfo,
  updateDeviceId,
  updatePageLoading,
  updateLanguage,
  updateUserInfo,
  updateRegion,
  clearGlobalStateSlice,
  updateShowCode,
  updateCompleted,
  updateCompleteInfoVisible,
  updateCompleteDone,
  updateNames,
  updateTimesLimit,
  updateIsScreenlt720,
  updateSignupMethod,
  updateLoginMethod,
  updateSubmitLoading,
  updateUserInfoAvatar,
  updateBindContactInformation,
  updateMediaUploadAgreement,
} = globalSlice.actions;

export const selectRegionName = (state: RootState) => {
  const region = state.globalReducer.region;
  const regionOptions = state.configReducer.regionOptions;
  return regionOptions.find((regionOption) => regionOption.value === region)?.name || "";
};

export const selectRegionIcon = (state: RootState) => {
  const region = state.globalReducer.region;
  const regionOptions = state.configReducer.regionOptions;
  return getRegionIcon(regionOptions, region);
};

export const selectRegionIconByName = (regionName: string) => (state: RootState) => {
  const regionOptions = state.configReducer.regionOptions;
  return regionOptions.find((region) => region.name === regionName)?.icon || "";
};

export const selectUserInfo = (state: RootState) => {
  return state.globalReducer.userInfo;
};

export const selectMediaUploadAgreement = (state: RootState) => {
  return state.globalReducer.mediaUploadAgreement;
};

export const selectIsMember = (state: RootState) => {
  return (
    state.globalReducer.membership?.level.toLocaleLowerCase() !== "trial" &&
    state.globalReducer.membership?.expired === false
  );
};

export const selectIsGray = (grayTag: string) => (state: RootState) => {
  return state.globalReducer.userInfo?.tags?.includes(grayTag);
};

export const useSelectLast7Days = () => {
  const { lastDay } = useAppSelector((state) => state.globalReducer);
  return [dayjs(lastDay).subtract(6, "days").format("YYYY-MM-DD"), lastDay];
};
export const useSelectLastRangeDays = (num = 7) => {
  const { lastDay } = useAppSelector((state) => state.globalReducer);
  return [
    dayjs(lastDay)
      .subtract(num - 1, "days")
      .format("YYYY-MM-DD"),
    lastDay,
  ];
};

export const selectUserBelongCountry = (state: RootState) => {
  return state.globalReducer.userInfo?.bizCountry;
};

const globalReducer = globalSlice.reducer;
export default globalReducer;

export const useIsChinaMember = () => {
  const userInfo = useAppSelector(selectUserInfo);
  return { isChinaMember: userInfo.bizCountry === "china" };
};

export const useIsCNIDVNMember = () => {
  const { userInfo } = useUserInfo();
  return {
    IsCNIDVNMember:
      userInfo.bizCountry === "china" || userInfo.bizCountry === "indonesia" || userInfo.bizCountry === "vietnam",
  };
};
